import {
  createTheme,
  PaletteMode,
  PaletteOptions,
  Shadows,
  Theme,
  ThemeOptions
} from '@mui/material'
import { deepmerge } from '@mui/utils'

import type { UiExperience } from 'common/experience/types'

import dark from './dark'
import light from './light'
import { nextComponents } from './nextComponents'
import { domainTokens } from './tokens/domain'
import { primitives } from './tokens/primitives'
import { SemanticTokens, semanticTokens } from './tokens/semantic'
import { legacyPaletteFromTokens } from './tokens/legacyMap'
import { typeStyle } from './typeStyle'

declare module '@mui/material/styles' {
  interface Theme {
    /** The experience the theme was built for; shared components read it to pick their look. */
    experience?: UiExperience
  }
  interface ThemeOptions {
    experience?: UiExperience
  }
}

const legacyOptions: Record<PaletteMode, ThemeOptions> = { light, dark }

type ThemeOverrides = Omit<ThemeOptions, 'palette'> & { palette: Partial<PaletteOptions> }
type ComponentsOptions = NonNullable<ThemeOptions['components']>

const { family, weight, style } = primitives.font
const { duration, easing } = primitives.motion

const typography: ThemeOptions['typography'] = {
  fontFamily: family.sans,
  fontSize: style.bodyMdRegular.fontSize,
  fontWeightRegular: weight.regular,
  fontWeightMedium: weight.medium,
  fontWeightBold: weight.semibold,
  // Legacy views render with this theme too and title their pages with h6, so h5-h6 take heading
  // sizes rather than display
  h1: typeStyle(style.displayLg),
  h2: typeStyle(style.displayLg),
  h3: typeStyle(style.displayLg),
  h4: typeStyle(style.displayLg),
  h5: typeStyle(style.headingXl),
  h6: typeStyle(style.headingLg),
  subtitle1: typeStyle(style.headingMd),
  subtitle2: typeStyle(style.bodyMdMedium),
  body1: typeStyle(style.bodyMdRegular),
  body2: typeStyle(style.bodySmRegular),
  button: typeStyle(style.labelButton),
  caption: typeStyle(style.captionRegular),
  overline: typeStyle(style.captionMedium)
}

/** MUI expects 25 shadow levels: 1-4 map to elevation 1, 5-12 to 2 (menus) and 13-24 to 3 (dialogs). */
const shadowsFromTokens = ({ elevation }: SemanticTokens) =>
  Array.from({ length: 25 }, (_, level) => {
    if (level === 0) return 'none'
    if (level <= 4) return elevation[1]
    if (level <= 12) return elevation[2]
    return elevation[3]
  }) as Shadows

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

const transitions: ThemeOptions['transitions'] = {
  duration: {
    shortest: duration.fast,
    shorter: duration.fast,
    short: duration.base,
    standard: duration.base,
    complex: duration.slow,
    enteringScreen: duration.base,
    leavingScreen: duration.fast
  },
  easing: { easeInOut: easing.standard, easeOut: easing.emphasized },
  ...(prefersReducedMotion() && { create: () => 'none' })
}

const nextOptions = (mode: PaletteMode): ThemeOverrides => {
  const tokens = semanticTokens[mode]
  const { bg, text, border, action, status } = tokens
  return {
    experience: 'next',
    palette: {
      primary: {
        main: action.primary.bg,
        dark: action.primary.bgHover,
        light: action.ghost.bgActive,
        contrastText: action.primary.text
      },
      error: {
        main: action.danger.bg,
        dark: action.danger.bgHover,
        contrastText: action.danger.text
      },
      success: { main: status.success.icon },
      warning: { main: status.warning.icon },
      info: { main: status.info.icon },
      // Legacy code reads `default` as a surface: both keys are the same color in the legacy theme.
      // The page color is `bg.page` (`--ppx-bg-page`).
      background: { default: bg.surface, paper: bg.surface },
      text: { primary: text.primary, secondary: text.secondary, disabled: text.disabled },
      divider: border.subtle,
      action: {
        hover: action.ghost.bgHover,
        selected: bg.subtle,
        disabled: action.disabled.text,
        disabledBackground: action.disabled.bg
      },
      ...legacyPaletteFromTokens(tokens)
    },
    typography,
    spacing: primitives.spacing,
    shape: { borderRadius: primitives.radius.none },
    shadows: shadowsFromTokens(tokens),
    transitions,
    components: nextComponents(tokens)
  }
}

/** A component the next theme restyles drops its legacy style overrides but keeps its legacy defaultProps. */
const mergeComponents = (
  legacy: ComponentsOptions = {},
  next: ComponentsOptions = {}
): ComponentsOptions =>
  Object.entries(next).reduce<ComponentsOptions>(
    (merged, [name, overrides]) => {
      const legacyDefaults = (legacy[name as keyof ComponentsOptions] as { defaultProps?: object })
        ?.defaultProps
      return {
        ...merged,
        [name]: { ...overrides, defaultProps: { ...legacyDefaults, ...overrides?.defaultProps } }
      }
    },
    { ...legacy }
  )

const buildOptions = (experience: UiExperience, mode: PaletteMode): ThemeOptions => {
  const legacy = legacyOptions[mode]
  if (experience !== 'next') return { ...legacy, experience }
  const { components, ...next } = nextOptions(mode)
  return {
    ...deepmerge(legacy, next),
    components: mergeComponents(legacy.components, components)
  }
}

const themeCache = new Map<string, Theme>()

/**
 * The next theme is the legacy one overridden by tokens, merged before `createTheme` so derived
 * values (`primary.dark`, typography variants) are recomputed from the new ones.
 */
export const createExperienceTheme = (experience: UiExperience, mode: PaletteMode): Theme => {
  const key = `${experience}-${mode}`
  const cached = themeCache.get(key)
  if (cached) return cached

  const theme = createTheme(buildOptions(experience, mode))
  themeCache.set(key, theme)
  return theme
}

const toKebabCase = (name: string) => name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)

const toCssVariables = (tokens: object, prefix: string): Record<string, string> =>
  Object.entries(tokens).reduce<Record<string, string>>(
    (variables, [name, value]) =>
      typeof value === 'object'
        ? { ...variables, ...toCssVariables(value, `${prefix}-${toKebabCase(name)}`) }
        : { ...variables, [`${prefix}-${toKebabCase(name)}`]: String(value) },
    {}
  )

/**
 * For surfaces MUI does not style (reactflow canvas, Monaco): the design system names prefixed
 * with `--ppx-`. `--pp-*` belongs to HTML interfaces.
 */
export const nextCssVariables = (mode: PaletteMode) =>
  toCssVariables({ ...semanticTokens[mode], ...domainTokens[mode] }, '--ppx')
